import * as React from 'react';

import { Box, Checkbox, FormControlLabel, Grid, Stack, Typography } from '@mui/material';

import { isEqual } from 'lodash';

import { ShippingRegion, UserAccessRequestResultValue } from '../../../graphql/API';
import useSubscriptionAccess from '../../../hooks/useSubscriptionAccess';
import Button from '../../Button/Button';
import { ChipOptionsProps } from '../../ChipOptions/ChipOptions';
import { BlackIOSSwitch } from '../../IOSSwitch';
import ChipOptionContainer from './ChipOptionContainer';
import {
  SHIPPING_AVAILABILITY,
  followerOptions,
  makeDefaultValue,
  makeFiltersOutput,
  ratingOptions,
  verifiedOptions,
} from './options';
import { PartialVenueListAccessRequestsFilter } from './types';

const shippingRegions = [
  ShippingRegion.us,
  ShippingRegion.asia,
  ShippingRegion.middleeast,
  ShippingRegion.eu,
  ShippingRegion.africa,
  ShippingRegion.latinamerica,
];

export type ProductRequestsFiltersProps = {
  filters?: PartialVenueListAccessRequestsFilter;
  onChange?: (filters: PartialVenueListAccessRequestsFilter | undefined) => void;
  onClose?: () => void;
  allShippingRegions?: ShippingRegion[];
};

type ChipOnChange = ChipOptionsProps['onChange'];

const ProductRequestsFilters: React.FC<ProductRequestsFiltersProps> = ({
  filters: defaultFilters,
  onChange,
  onClose,
  allShippingRegions = [],
}) => {
  const subscriptionAccess = useSubscriptionAccess();

  const [filters, setFilters] = React.useState<PartialVenueListAccessRequestsFilter | undefined>(
    defaultFilters,
  );

  const onApply = () => {
    if (onChange) onChange(filters);
    if (onClose) onClose();
  };

  const { fOptions, vOptions } = React.useMemo(
    () => ({
      fOptions: followerOptions(subscriptionAccess),
      vOptions: verifiedOptions(subscriptionAccess),
    }),
    [subscriptionAccess],
  );

  const handleOnChange = React.useCallback<ChipOnChange>(
    (e) => {
      const outputFilters = makeFiltersOutput(filters)[e.name](e.value);
      setFilters(outputFilters);
    },
    [filters],
  );

  const handleOnMarketChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, region: ShippingRegion) => {
      const checked = e.target.checked;
      const currentRegions = filters?.marketRegions || [];
      const isGlobalOn =
        currentRegions.length === 0 || currentRegions.length === shippingRegions.length;

      // Don't allow changes when global is on
      if (isGlobalOn) {
        return;
      }

      if (checked) {
        setFilters((prev) => ({
          ...prev,
          marketRegions: [...currentRegions, region],
        }));
      } else {
        setFilters((prev) => ({
          ...prev,
          marketRegions: currentRegions.filter((r) => r !== region),
        }));
      }
    },
    [filters?.marketRegions],
  );

  const onResetFilters = () => {
    setFilters({
      status: UserAccessRequestResultValue.requested,
      marketRegions: [],
      sortBy: 'createdAt',
      sortDirection: 'desc',
    });
  };

  const marginBottomButtons = (filters?.marketRegions?.length || 0) === 0 ? 16 : 5;

  return (
    <Box px={1}>
      <Stack spacing={4}>
        <Box display={'flex'} flexDirection={'column'} gap={'10px'}>
          <Typography component='div' variant='caption' fontWeight='500' fontSize={'15px'}>
            Requests from
          </Typography>

          <Box>
            <FormControlLabel
              sx={{
                m: 0,
                gap: '6px',
                fontSize: '4px',
                '& .MuiFormControlLabel-label': {
                  fontSize: '15px',
                },
              }}
              control={
                <BlackIOSSwitch
                  checked={
                    filters?.marketRegions?.length === 0 ||
                    filters?.marketRegions?.length === shippingRegions.length
                  }
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setFilters((prev) => ({
                        ...prev,
                        marketRegions: [...shippingRegions],
                      }));
                    } else {
                      setFilters((prev) => ({
                        ...prev,
                        marketRegions: [ShippingRegion.us],
                      }));
                    }
                  }}
                />
              }
              label={SHIPPING_AVAILABILITY['global']}
              labelPlacement='start'
            />
          </Box>
            <Box>
              <Grid container>
                {shippingRegions.map((value) => {
                  const isGlobalOn =
                    filters?.marketRegions?.length === 0 ||
                    filters?.marketRegions?.length === shippingRegions.length;
                  return (
                    <Grid item xs={4} key={value}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={isGlobalOn}
                            checked={
                              isGlobalOn || filters?.marketRegions?.includes(value)
                            }
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              handleOnMarketChange(e, value)
                            }
                            sx={{ color: '#DADADA' }}
                          />
                        }
                        label={SHIPPING_AVAILABILITY[value]}
                        sx={{
                          '& .MuiSvgIcon-root': { fontSize: 24 },
                          '& .MuiFormControlLabel-label': {
                            fontSize: '15px',
                          },
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
        </Box>

        <ChipOptionContainer
          defaultValue={makeDefaultValue['follower'](filters)}
          name='follower'
          label='Followers'
          options={fOptions}
          onChange={handleOnChange}
        />

        <ChipOptionContainer
          defaultValue={makeDefaultValue['rating'](filters)}
          name='rating'
          label='Rating'
          options={ratingOptions}
          onChange={handleOnChange}
        />

        <ChipOptionContainer
          verifiedIcon
          defaultValue={makeDefaultValue['instagramVerified'](filters)}
          name='instagramVerified'
          label='Verified on Instagram'
          options={vOptions}
          onChange={handleOnChange}
        />
      </Stack>

      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        mt={marginBottomButtons}
      >
        <Button sx={{ textDecoration: 'underline' }} onClick={onResetFilters}>
          Reset Filters
        </Button>
        <Box display={'flex'} alignItems={'center'} gap={'1rem'}>
          <Button variant='outlined' sx={{ padding: '6px 20px' }} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant='contained'
            sx={{ padding: '6px 20px' }}
            onClick={onApply}
            disabled={isEqual(defaultFilters, filters)}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductRequestsFilters;
